import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Search } from 'lucide-react'
import { cx } from '@/lib/cx'
import type { Emoji, ReactionEmoji, Sticker, StickerItem } from '@/types/api'
import type { Gif } from '@/lib/gifsApi'
import { EmojiGrid } from './EmojiGrid'
import { StickerGrid, type StickerLock } from './StickerGrid'
import { GifGrid } from './GifGrid'
import styles from './MediaPicker.module.css'

export type PickerTab = 'gif' | 'sticker' | 'emoji'

const TABS: { id: PickerTab; label: string; placeholder: string }[] = [
  { id: 'gif', label: 'Гифки', placeholder: 'Поиск гифок' },
  { id: 'sticker', label: 'Стикеры', placeholder: 'Поиск стикера' },
  { id: 'emoji', label: 'Эмодзи', placeholder: 'Поиск эмодзи' },
]

interface Props {
  tab: PickerTab
  onTab: (tab: PickerTab) => void
  stickers: { guild: string; stickers: Sticker[]; locked?: boolean }[]
  /** Стикеры других серверов закрыты: нет Кличат Плюса или сервер их запретил. */
  stickerLock?: StickerLock
  onGetPlus?: (() => void) | undefined
  emoji: { guild: string; emojis: Emoji[] }[]
  /** Гифки есть не всегда: без ключа Klipy вкладку не показываем. */
  gifs: boolean
  onSticker: (sticker: StickerItem) => void
  onEmoji: (emoji: ReactionEmoji) => void
  onGif: (gif: Gif) => void
  onClose: () => void
}

/**
 * Одна панель на гифки, стикеры и эмодзи — как в Discord. Вкладки переключаются и сверху,
 * и кнопками у поля ввода; повторное нажатие на ту же кнопку панель закрывает.
 * Клик мимо закрывает, но кнопки-переключатели из этого исключены: иначе панель успевала
 * закрыться по mousedown и тут же открыться по click — и мигала.
 */
export function MediaPicker({ tab, onTab, stickers, stickerLock = null, onGetPlus, emoji, gifs, onSticker, onEmoji, onGif, onClose }: Props) {
  const [queries, setQueries] = useState<Record<PickerTab, string>>({ gif: '', sticker: '', emoji: '' })
  const ref = useRef<HTMLDivElement | null>(null)
  const input = useRef<HTMLInputElement | null>(null)
  const tabs = gifs ? TABS : TABS.filter((t) => t.id !== 'gif')
  const current = tabs.find((t) => t.id === tab) ?? tabs[0]!
  const query = queries[current.id]
  const setQuery = (value: string) => setQueries((q) => ({ ...q, [current.id]: value }))

  useEffect(() => {
    input.current?.focus()
  }, [current.id])

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (ref.current?.contains(target) || target.closest('[data-picker-trigger]')) return
      onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [onClose])

  return (
    <div
      className={cx(styles.picker, 'klichat-popover')}
      style={{ '--popover-origin': 'bottom right' } as CSSProperties}
      ref={ref}
      role="dialog"
      aria-label="Гифки, стикеры и эмодзи"
    >
      <div className={styles.tabs} role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={t.id === current.id}
            className={cx(styles.tab, t.id === current.id && styles.tabActive)}
            onClick={() => onTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className={styles.search}>
        <Search size={16} className={styles.searchIcon} />
        <input
          ref={input}
          className={styles.input}
          placeholder={current.placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label={current.placeholder}
        />
      </div>
      <div className={cx(styles.body, 'scroller')}>
        {current.id === 'gif' ? <GifGrid query={query} onQuery={setQuery} onPick={onGif} /> : null}
        {current.id === 'sticker' ? <StickerGrid groups={stickers} query={query} lock={stickerLock} onGetPlus={onGetPlus} onPick={onSticker} /> : null}
        {current.id === 'emoji' ? <EmojiGrid custom={emoji} query={query} onPick={onEmoji} /> : null}
      </div>
    </div>
  )
}
